import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { first, isNil, max, sortBy } from 'lodash';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, startWith, tap } from 'rxjs/operators';

import {
  clearTurnoverKanbanPortfolio,
  joinProperties,
  selectedPortfolioId,
  selectedPropertyId,
} from '@dashboards/actions/dashboard.actions';
import { selectJoinProperties, selectSelectedPortfolioId } from '@dashboards/selectors/dashboards.selectors';
import { selectPortfolios, selectProperties } from '@portfolio/selectors/portfolio.selectors';
import { DashboardViewType } from '@shared/enums/dashboard-view-type';
import { RouteData } from '@shared/enums/route-data';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestPortfolioModel } from '@shared/interfaces/rest-portfolio-model.interface';

@UntilDestroy()
@Component({
  selector: 'app-property-selector',
  templateUrl: './property-selector.component.html',
  styleUrls: ['./property-selector.component.scss'],
})
export class PropertySelectorComponent implements OnInit {
  userProperties: IRadioButtonOption<number>[] = [];
  userPortfolios: IRadioButtonOption<number>[] = [];
  groupingByItems: IRadioButtonOption<boolean>[] = [];

  DashboardViewType = DashboardViewType;

  urlPortfolioId: number = null;
  urlPropertyId: number = null;

  selectorsWidthCss = '';
  maxNameLength = 0;
  maxNameLength$ = new BehaviorSubject(0);

  dashboardViewType = DashboardViewType.PORTFOLIO_WIDE_VIEW;

  properties: UntypedFormControl = new UntypedFormControl();
  portfolio: UntypedFormControl = new UntypedFormControl();
  grouping: UntypedFormControl = new UntypedFormControl(2);

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<{}>,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    const { queryParams } = this.activatedRoute.snapshot;
    this.urlPortfolioId = parseInt(queryParams?.[RouteData.PORTFOLIO_ID], 10) || null;
    this.urlPropertyId = parseInt(queryParams?.[RouteData.PROPERTY_ID], 10) || null;
    this.maxNameLength$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(value => {
          this.maxNameLength = Math.max(this.maxNameLength, value);
          const maxWidth = this.maxNameLength * 6.31 + 90;
          let targetWidth = 200;
          while (targetWidth < maxWidth) {
            targetWidth += 10;
          }
          this.selectorsWidthCss = `width-${targetWidth}`;
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    combineLatest([this.activatedRoute.queryParams, this.store.select(selectSelectedPortfolioId)])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(([queryParams, portfolioId]) => {
          this.urlPortfolioId = parseInt(queryParams?.[RouteData.PORTFOLIO_ID], 10) || portfolioId;
          this.urlPropertyId = parseInt(queryParams?.[RouteData.PROPERTY_ID], 10) || null;

          this.groupingByItems = [
            { label: 'None', value: true },
            { label: 'By property', value: false },
          ];
        })
      )
      .subscribe();

    this.store
      .select(selectJoinProperties)
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(joined => {
          this.grouping.setValue(joined);
        })
      )
      .subscribe();

    this.store
      .select(selectPortfolios)
      .pipe(
        untilDestroyed(this),
        filter(portfolios => !!portfolios?.length),
        tap((portfolios: RestPortfolioModel[]) => {
          this.userPortfolios = portfolios.map<IRadioButtonOption<number>>(item => {
            return {
              value: item.id,
              label: item.name,
            };
          });
          this.maxNameLength$.next(max(this.userPortfolios.map(x => x.label.length)));
          if (this.urlPortfolioId && this.userPortfolios.some(e => e.value == this.urlPortfolioId)) {
            this.portfolio.setValue(this.urlPortfolioId, { emitEvent: false });
          } else {
            this.portfolio.setValue(first(this.userPortfolios || [])?.value);
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    combineLatest([this.store.select(selectSelectedPortfolioId), this.store.select(selectProperties)])
      .pipe(
        untilDestroyed(this),
        filter(([portfolioId, properties]) => !!portfolioId && !!properties?.length),
        tap(([portfolioId, properties]) => {
          this.maxNameLength = 0;
          this.userProperties = [
            {
              value: -1,
              label: 'All properties',
              sort: 0,
            },
          ];
          const options = properties
            .filter(x => x.portfolioId === portfolioId)
            .map<IRadioButtonOption<number>>((item, index) => {
              return {
                value: item.id,
                label: item.name,
                sort: index + 1,
              };
            });
          this.userProperties.push(...sortBy(options, [item => item.label.toUpperCase()]));
          this.maxNameLength$.next(max(this.userProperties.map(x => x.label.length)));
          if (this.urlPropertyId && this.userProperties.some(e => e.value == this.urlPropertyId)) {
            this.properties.setValue(this.urlPropertyId, { emitEvent: false });
          } else {
            this.properties.setValue(this.userProperties[0]?.value);
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap(route => route.data),
        tap(data => {
          const newDashboardViewType = (
            isNil(data?.[RouteData.DASHBOARD_VIEW_TYPE])
              ? DashboardViewType.NONE
              : data?.[RouteData.DASHBOARD_VIEW_TYPE]
          ) as DashboardViewType;
          if (this.dashboardViewType !== newDashboardViewType) {
            this.dashboardViewType = newDashboardViewType;
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe();

    this.portfolio.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(portfolioItem => !isNil(portfolioItem)),
        tap((portfolioId: number) => {
          this.updateRouterParam({ [RouteData.PORTFOLIO_ID]: portfolioId, [RouteData.PROPERTY_ID]: null });
          // clear cached kanban
          this.store.dispatch(clearTurnoverKanbanPortfolio());
          this.store.dispatch(selectedPortfolioId({ portfolioId }));
        })
      )
      .subscribe();

    combineLatest([this.properties.valueChanges.pipe(distinctUntilChanged()), this.store.select(selectProperties)])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(([propertyItem, properties]) => !isNil(propertyItem) && !!properties?.length),
        map(([propertyId, properties]) => {
          return properties?.find(p => p.id === propertyId);
        }),
        tap(property => {
          if (
            [DashboardViewType.PROPERTY_VIEW, DashboardViewType.PROPERTY_WITH_GROUPING_VIEW].includes(
              this.dashboardViewType
            )
          ) {
            this.updateRouterParam({
              [RouteData.PORTFOLIO_ID]: this.portfolio.value,
              [RouteData.PROPERTY_ID]: property?.id,
            });
          }
          this.store.dispatch(selectedPropertyId({ propertyId: property?.id }));
        })
      )
      .subscribe();

    this.grouping.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(state => {
          this.store.dispatch(joinProperties({ state }));
        })
      )
      .subscribe();
  }

  private updateRouterParam(params: Params) {
    const currentParams = this.activatedRoute.snapshot.queryParams;
    const currentFragment = this.activatedRoute.snapshot.fragment;
    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...currentParams, ...params },
        queryParamsHandling: 'merge',
        replaceUrl: true,
        fragment: currentFragment,
      })
      .then();
  }
}
